import { http } from '@/utils/request'
import type * as T from '@/types'

// ==================== Shared ====================
export type ImportItem = {
  order_no: string
  company: string
  number: string
  track_count: number
  error?: string
}
export type ImportLogisticsResult = {
  success: number
  fail: number
  succeeded: ImportItem[]
  failed: ImportItem[]
}

// ==================== Auth ====================
export const authApi = {
  login: (data: T.LoginReq) => http.post<T.LoginResp>('/auth/login', data),
  getInfo: () => http.get<T.UserInfoResp>('/auth/info'),
  changePassword: (data: { old_password: string; new_password: string }) =>
    http.post('/auth/password', data),
}

// ==================== TOTP 安全验证（谷歌验证码）====================
export const totpApi = {
  // 获取当前 TOTP 绑定状态
  getStatus: () => http.get<{
    bound: boolean
    bound_at?: string
    otp_auth?: string
    qr_base64?: string
  }>('/totp/status'),

  // 生成分 TOTP 密钥（绑定第一步）
  generate: () => http.post<{
    secret: string
    otp_auth: string
    qr_base64: string
  }>('/totp/generate', {}),

  // 确认绑定（绑定第二步：输入验证码）
  confirm: (code: string) => http.post<{
    bound: boolean
    recovery_codes: string[]
    message: string
  }>('/totp/confirm', { code }),

  // 验证 TOTP 码，获取临时操作令牌
  verify: (code: string) => http.post<{ token: string }>('/totp/verify', { code }),

  // 解绑 TOTP
  unbind: (code: string) => http.post('/totp/unbind', { code }),

  // 获取恢复码
  getRecoveryCodes: (code: string) => http.post<{
    recovery_codes: string[]
    message: string
  }>('/totp/recovery-codes', { code }),
}

// ==================== User ====================
export const userApi = {
  list: (params: T.PageParams) => http.get<T.PageResult<T.AdminUser>>('/system/user', params),
  create: (data: any) => http.post('/system/user', data),
  update: (data: any) => http.post('/system/user', data), // ID在body中
  delete: (id: number) => http.post(`/system/user/${id}/delete`),
  resetPassword: (id: number, data: { password: string }) =>
    http.post(`/system/user/${id}/reset-password`, data),
}

// ==================== Role ====================
export const roleApi = {
  list: (params: T.PageParams) => http.get<T.PageResult<T.AdminRole>>('/system/role', params),
  all: () => http.get<T.AdminRole[]>('/system/role/all'),
  detail: (id: number) => http.get<T.AdminRole>(`/system/role/${id}`),
  create: (data: any) => http.post('/system/role', data),
  update: (data: any) => http.post('/system/role', data), // ID在body中
  delete: (id: number) => http.post(`/system/role/${id}/delete`),
}

// ==================== Menu ====================
export const menuApi = {
  tree: () => http.get<T.MenuItem[]>('/system/menu/tree'),
  detail: (id: number) => http.get<T.MenuItem>(`/system/menu/${id}`),
  create: (data: any) => http.post('/system/menu', data),
  update: (data: any) => http.post('/system/menu/update', data), // ID在body中
  delete: (id: number) => http.post(`/system/menu/${id}/delete`),
}

// ==================== Log ====================
export const logApi = {
  list: (params: T.PageParams & { user_id?: number }) =>
    http.get<T.PageResult<T.OperationLog>>('/system/log', params),
}

// ==================== Cache ====================
export const cacheApi = {
  clear: () => http.post<{ cleared: number }>('/system/cache/clear', {}),
}

// ==================== Product SPU ====================
export const spuApi = {
  list: (params: T.PageParams & { merchant_id?: number; category_id?: number; brand_id?: number; status?: number }) =>
    http.get<T.PageResult<T.ProductSpu>>('/product/spu', params),
  detail: (id: number) => http.get<T.ProductSpu>(`/product/spu/${id}`),
  create: (data: any) => http.post<{ id: number }>('/product/spu', data),
  update: (data: any) => http.post('/product/spu', data), // ID在body中
  changeStatus: (data: { id: number; status: number }) => http.post('/product/spu/status', data),
  delete: (id: number) => http.delete(`/product/spu/${id}`),
  /** 商品选择器专用：返回全部已上架商品（支持关键词、分类、分页） */
  picker: (params?: { keyword?: string; category_id?: number; page?: number; page_size?: number }) =>
    http.get<{ list: T.ProductSpu[]; total: number; page: number; page_size: number }>(
      '/product/spu/picker', params ?? {}
    ),
}

// ==================== 商品批量导入 ====================
export const productImportApi = {
  upload: (merchantId: number, excel: File, zip: File) => {
    const form = new FormData()
    form.append('merchant_id', String(merchantId))
    form.append('excel', excel)
    form.append('zip', zip)
    return http.post<{ task_id: number }>('/product/import', form)
  },
  listTasks: (params: T.PageParams & { merchant_id?: number; status?: number }) =>
    http.get<T.PageResult<T.ProductImportTask>>('/product/import/tasks', params),
  taskDetail: (id: number) => http.get<T.ProductImportTask>(`/product/import/tasks/${id}`),
}

// ==================== Category ====================
export const categoryApi = {
  tree: () => http.get<T.ProductCategory[]>('/product/category/tree'),
  create: (data: any) => http.post<{ id: number }>('/product/category', data),
  update: (data: any) => http.post('/product/category/update', data), // ID在body中
  delete: (id: number) => http.post(`/product/category/${id}/delete`),
}

// ==================== Brand ====================
export const brandApi = {
  list: (params: T.PageParams) => http.get<T.PageResult<T.ProductBrand>>('/product/brand', params),
  create: (data: any) => http.post<{ id: number }>('/product/brand', data),
  update: (data: any) => http.post('/product/brand', data), // ID在body中
  delete: (id: number) => http.post(`/product/brand/${id}/delete`),
}

// ==================== Attr ====================
export const attrApi = {
  groups: (category_id?: number) => http.get<T.AttrGroup[]>('/product/attr/group', { category_id }),
  groupDetail: (id: number) => http.get<T.AttrGroup>(`/product/attr/group/${id}`),
  createGroup: (data: any) => http.post<{ id: number }>('/product/attr/group', data),
  updateGroup: (data: any) => http.post('/product/attr/group/update', data), // ID在body中
  deleteGroup: (id: number) => http.post(`/product/attr/group/${id}/delete`),
  list: (params: { group_id?: number; type?: number }) =>
    http.get<T.ProductAttr[]>('/product/attr/list', params),
  create: (data: any) => http.post<{ id: number }>('/product/attr', data),
  update: (data: any) => http.post('/product/attr/update', data), // ID在body中
  delete: (id: number) => http.post(`/product/attr/${id}/delete`),
  values: (attrId: number) => http.get<T.AttrValue[]>(`/product/attr/${attrId}/values`),
  saveValues: (attrId: number, values: T.AttrValue[]) =>
    http.post(`/product/attr/${attrId}/values`, values),
}

// ==================== Merchant ====================
export const merchantApi = {
  list: (params: T.PageParams & { status?: number; account?: string }) =>
    http.get<T.PageResult<T.Merchant>>('/merchant', params),
  listForTask: (params: T.PageParams & {
    keyword?: string
    account?: string
    created_from?: string
    created_to?: string
    register_source?: string
    register_source_in?: string[]
    has_booth?: 0 | 1
    has_ad_slot?: 0 | 1
    in_active_campaign?: 0 | 1 // 是否正在推广（ad_campaign 表 status=0 且当前在 start_at~end_at 之间）
    join_days?: number   // >0=N天内，<0=超过N天
    slot_count_gte?: number // 展示位数量 >= N
  }) => http.get<T.PageResult<T.Merchant>>('/merchant/list-for-task', params),
  detail: (id: number | string) => http.get<T.Merchant>(`/merchant/${id}`),
  create: (data: any) => http.post<{ id: string }>('/merchant', data),
  update: (data: any) => http.post('/merchant', data), // ID在body中
  updateAdminUser: (merchantId: number | string, data: any) =>
    http.put(`/merchant/${merchantId}/admin-user`, data),
  resetPassword: (merchantId: number | string, data: { password: string }) =>
    http.post(`/merchant/${merchantId}/reset-password`, data),
  resetPayword: (merchantId: number | string, data: { payword: string }) =>
    http.post(`/merchant/${merchantId}/reset-payword`, data),
  audit: (data: { id: number; status: number }) => http.post('/merchant/audit', data),
}

// ==================== 内部测试商户标记 ====================
export const testMerchantApi = {
  list: (params: T.PageParams) =>
    http.get<T.PageResult<T.TestMerchantListItem>>('/system/test-merchant', params),
  create: (data: { merchant_id: number; remark?: string }) =>
    http.post('/system/test-merchant/manage', data),
  updateRemark: (data: { id: number; remark: string }) =>
    http.post('/system/test-merchant/manage/remark', data),
  delete: (id: number) =>
    http.post('/system/test-merchant/manage/delete', { id }),
}

// ==================== 商户积分/余额管理（后台管理员，需 TOTP 验证）====================
export const merchantFundApi = {
  get: (merchantId: number | string) =>
    http.get<{ merchant_id: number | string; points: number; balance: number | string }>(`/merchant/${merchantId}/fund`),
  increase: (merchantId: number | string, data: { target: 'points' | 'balance'; amount: number | string; reason: string }, economicKey: string) =>
    http.post<{ adjustment_id: number | string; balance_before: number | string; balance_after: number | string }>(
      `/merchant/${merchantId}/fund/increase`, data, { headers: { 'Idempotency-Key': economicKey } },
    ),
  decrease: (merchantId: number | string, data: { target: 'points' | 'balance'; amount: number | string; reason: string }, economicKey: string) =>
    http.post<{ adjustment_id: number | string; balance_before: number | string; balance_after: number | string }>(
      `/merchant/${merchantId}/fund/decrease`, data, { headers: { 'Idempotency-Key': economicKey } },
    ),
  listAdjustments: (params: T.PageParams & {
    merchant_id?: number
    admin_id?: number
    target?: 'points' | 'balance'
    start_time?: string
    end_time?: string
  }) => http.get<T.PageResult<{
    id: number
    merchant_id: number
    merchant_name: string
    admin_id: number
    admin_username: string
    target: 'points' | 'balance'
    direction: 'increase' | 'decrease'
    amount: number
    reason: string
    balance_before: number
    balance_after: number
    created_at: string
  }>>('/merchant/fund/adjustments', params),
  balanceLogs: (merchantId: number, params: T.PageParams & { type?: number }) =>
    http.get<T.PageResult<T.MerchantBalanceLog>>(`/merchant/${merchantId}/balance-logs`, params),
  pointsLogs: (merchantId: number, params: T.PageParams) =>
    http.get<T.PageResult<T.MerchantPointsLog>>(`/merchant/${merchantId}/points-logs`, params),
  // 账单查询/积分查询页面用：不限定商户，默认展示全部商户流水第一页
  allBalanceLogs: (params: T.PageParams & { merchant_id?: number; type?: number; business_type?: string }) =>
    http.get<T.PageResult<T.MerchantBalanceLog & { merchant_email: string }>>('/merchant/balance-logs', params),
  allPointsLogs: (params: T.PageParams & { merchant_id?: number; type?: number; business_type?: string }) =>
    http.get<T.PageResult<T.MerchantPointsLog & { merchant_email: string }>>('/merchant/points-logs', params),
}

// ==================== 商户展示位管理（后台管理员代理视角）====================
export const merchantSlotAdminApi = {
  // 获取商户的所有展示位（含商品数量）
  listSlots: (merchantId: number) =>
    http.post<{ list: T.MerchantSlotWithCount[]; total: number }>('/admin/merchant/slots', { merchant_id: merchantId }),
  // 获取展示位的商品列表（含 SPU 详情）
  listProducts: (merchantId: number, slotId: number) =>
    http.post<T.SlotProductWithSpu[]>('/admin/merchant/slot-products', { merchant_id: merchantId, slot_id: slotId }),
  // 获取商户商品列表（分页，用于选择）
  listMerchantProducts: (merchantId: number, params?: { keyword?: string; page?: number; page_size?: number }) =>
    http.post<{ list: T.ProductSpu[]; total: number; page: number; page_size: number }>(
      '/admin/merchant/products', { merchant_id: merchantId, ...params }
    ),
  // 添加商品到展示位
  addProduct: (merchantId: number, slotId: number, data: { spu_id: number; sort?: number }) =>
    http.post('/admin/merchant/slot-product/add', { merchant_id: merchantId, slot_id: slotId, ...data }),
  // 从展示位移除商品
  removeProduct: (merchantId: number, slotId: number, data: { spu_id: number }) =>
    http.post('/admin/merchant/slot-product/remove', { merchant_id: merchantId, slot_id: slotId, ...data }),
  // 随机放置商品到指定展示位
  randomSlot: (merchantId: number, slotId: number, data?: { count?: number }) =>
    http.post<{ added: number }>('/admin/merchant/slot/random', { merchant_id: merchantId, slot_id: slotId, ...data }),
  // 随机放置商品到所有展示位
  randomAllSlots: (merchantId: number, data?: { count?: number }) =>
    http.post<{ slots_updated: number; total_added: number }>('/admin/merchant/slot/random-all', { merchant_id: merchantId, ...data }),
  // 激活码扩展展位
  extendByCode: (code: string, slotId?: number) =>
    http.post<{ slot_id: number; name: string; message: string }>('/admin/merchant/slot/code/extend', { code, slot_id: slotId }),
}

// ==================== 物流 ====================
export const logisticsApi = {
  companies: () => http.get<any[]>('/logistics/company'),
  createCompany: (data: any) => http.post('/logistics/company', data),
  freightList: (merchant_id?: number) =>
    http.get<T.FreightTemplate[]>('/logistics/freight', { merchant_id }),
  freightDetail: (id: number) => http.get<T.FreightTemplate>(`/logistics/freight/${id}`),
  createFreight: (data: any) => http.post('/logistics/freight', data),
  updateFreight: (data: any) => http.post('/logistics/freight/update', data), // ID在body中
  deleteFreight: (id: number) => http.post(`/logistics/freight/${id}/delete`),
  regionTree: () => http.get('/logistics/region/tree'),
  track: (company: string, number: string) =>
    http.get<{ company: string; number: string; state: string; traces: { time: string; status: string }[] }>(
      '/logistics/track', { company, number }
    ),
  // 导入物流
  importSingle: (data: {
    order_no: string
    logistics_company: string
    logistics_no: string
    tracks: { time: string; status: string; location?: string }[]
  }) => http.post<ImportLogisticsResult>('/logistics/import/single', data),
  importFile: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return http.post<ImportLogisticsResult>('/logistics/import/file', form)
  },
  // 分组导入
  importGrouped: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return http.post<any>('/logistics/import/grouped', form)
  },
  listImportGroups: (page = 1, pageSize = 20) =>
    http.get<{ list: any[]; total: number; page: number; page_size: number }>(
      '/logistics/import/grouped', { page, page_size: pageSize }
    ),
  getImportGroup: (id: number) =>
    http.get<{ group: any; items: any[] }>(`/logistics/import/grouped/${id}`),
  deleteImportGroup: (id: number) =>
    http.delete(`/logistics/import/grouped/${id}`),
}

// ==================== Payment ====================
export const paymentApi = {
  channels: () => http.get('/payment/channel'),
  updateChannel: (data: any) => http.post('/payment/channel/update', data), // ID在body中
  getConfig: (id: number) =>
    http.get<{ id: number; code: string; name: string; config: Record<string, any> }>(`/payment/channel/config?id=${id}`),
  saveConfig: (data: { id: number; config: Record<string, any> }) =>
    http.post('/payment/channel/config', data),
}

// ==================== i18n ====================
export const i18nApi = {
  languages: () => http.get('/i18n/lang'),
  translations: (params: { entity_type: string; entity_id: number; lang_code?: string }) =>
    http.get<T.Translation[]>('/i18n/translation', params),
  saveTranslation: (data: any) => http.post('/i18n/translation', data),
  deleteTranslation: (id: number) => http.post(`/i18n/translation/${id}/delete`),
}

// ==================== Activation Code ====================
export const activationCodeApi = {
  list: (params: T.PageParams & { status?: number; user_type?: number; user_id?: number }) =>
    http.get<T.PageResult<T.ActivationCode>>('/activation-code', params),
  generate: (data: { count: number; user_type: number; expire_at?: string; remark?: string }) =>
    http.post<{ count: number; codes: T.ActivationCode[] }>('/activation-code/generate', data),
  disable: (data: { id: number }) => http.post('/activation-code/disable', data),
}

// ==================== API 加密配置 ====================
export const encryptConfigApi = {
  get: () => http.get<T.EncryptConfig>('/system/encrypt-config'),
  save: (data: T.EncryptConfig) => http.post('/system/encrypt-config', data),
}

// ==================== 注册配置 ====================
export const registerConfigApi = {
  get: () => http.get<T.RegisterConfig>('/system/register-config'),
  save: (data: T.RegisterConfig) => http.post('/system/register-config', data),
}

// ==================== 服务器线路 ====================
export const serverLineApi = {
  list: () => http.get<T.ServerLine[]>('/system/server-line'),
  create: (data: Partial<T.ServerLine>) => http.post<{ id: number }>('/system/server-line', data),
  update: (data: Partial<T.ServerLine>) => http.post('/system/server-line', data), // ID在body中
  delete: (id: number) => http.post(`/system/server-line/${id}/delete`),
}

// ==================== 邮件配置 ====================
export const emailConfigApi = {
  get: () => http.get<{ host: string; port: number; username: string; password: string; from: string; ssl: boolean }>('/system/email-config'),
  save: (data: { host: string; port: number; username: string; password: string; from: string; ssl: boolean }) =>
    http.post('/system/email-config', data),
  test: (to: string) => http.post('/system/email-config/test', { to }),
}

// ==================== VIP商户收益配置 ====================
export const benefitConfigApi = {
  get: () => http.get<{ vip_bonus: string; vip_price: string; delivery_date: string; gcc_ratio: string; gcc_fallback_price: string }>('/system/benefit-config'),
  save: (data: { vip_bonus: string; vip_price: string; delivery_date: string; gcc_ratio: string; gcc_fallback_price: string }) => http.post('/system/benefit-config', data),
}

// ==================== 提现页文字配置 ====================
export const withdrawPageConfigApi = {
  get: () => http.get<{ notice: string }>('/system/withdraw-page-config'),
  save: (data: { notice: string }) => http.post('/system/withdraw-page-config', data),
}

// ==================== 分销平台配置（全平台统一一份，不区分商户） ====================
export type DistributionConfig = {
  id: number
  merchant_id: number
  is_enabled: number
  max_level: number
  auto_become: number
  auto_become_condition: unknown
  bind_mode: number
  bind_duration: number
  need_audit: number
  allow_self_purchase: number
  settlement_days: number
  min_withdraw_amount: number
  withdraw_fee_rate: number
  withdraw_auto_audit: number
  complaint_email: string
  invite_url_prefix: string
}
export const distributionConfigApi = {
  get: () => http.get<DistributionConfig>('/distribution/config'),
  save: (data: Partial<DistributionConfig>) => http.post('/distribution/config', data),
}

// ==================== 启动页配置 ====================
export const splashApi = {
  get: (merchant_id: number) => http.get<T.SplashConfig>(`/system/splash?merchant_id=${merchant_id}`),
  save: (data: T.SplashConfig) => http.post('/system/splash', data),
}

// ==================== 协议文档 ====================
export const protocolApi = {
  list: (merchant_id: number) => http.get<T.AppProtocol[]>(`/system/protocol?merchant_id=${merchant_id}`),
  save: (data: T.AppProtocol) => http.post('/system/protocol', data),
  delete: (id: number) => http.post(`/system/protocol/${id}/delete`),
}

// ==================== 会话管理（设备/踢人）====================
export const sessionApi = {
  listCustomer: (params: T.PageParams & { customer_id?: number; device_type?: string }) =>
    http.get<T.PageResult<T.CustomerSessionItem>>('/system/session/customer', params),
  kickCustomer: (id: number) => http.post(`/system/session/customer/${id}`),
  kickAllCustomer: (customer_id: number) =>
    http.post('/system/session/customer/logout-all', { customer_id }),
  listMerchant: (params: T.PageParams & { merchant_id?: number; device_type?: string }) =>
    http.get<T.PageResult<T.MerchantSessionItem>>('/system/session/merchant', params),
  kickMerchant: (id: number) => http.post(`/system/session/merchant/${id}`),
  kickAllMerchant: (merchant_id: number) =>
    http.post('/system/session/merchant/logout-all', { merchant_id }),
}

// ==================== 分销 - 店长管理 ====================
export const shopOwnerApi = {
  list: (params: T.PageParams & { merchant_id?: number }) =>
    http.get<T.PageResult<T.ShopOwner>>('/distribution/shop-owner', params),
  detail: (id: number) => http.get<T.ShopOwner>(`/distribution/shop-owner/${id}`),
  customers: (id: number, params: T.PageParams) =>
    http.get<T.PageResult<T.ShopOwnerCustomer>>(`/distribution/shop-owner/${id}/customers`, params),
  reports: (id: number) =>
    http.get<T.ShopOwnerMonthlyReport[]>(`/distribution/shop-owner/${id}/reports`),
  dashboard: (id: number) =>
    http.get<T.ShopOwnerDashboardResp>(`/distribution/shop-owner/${id}/dashboard`),
}

// ==================== 分销 - 提现审核 ====================
export const withdrawalApi = {
  list: (params: T.PageParams & { type?: 'distributor' | 'merchant' | 'all'; status?: number }) =>
    http.get<T.PageResult<T.Withdrawal>>('/distribution/withdrawal', params),
  audit: (data: { id: number; status: 1 | 4; remark?: string }) =>
    http.post('/distribution/withdrawal/audit', data),
  markTransferring: (data: { id: number; transfer_no: string }) =>
    http.post('/distribution/withdrawal/mark-transferring', data),
  billSummary: (id: number) =>
    http.get<T.MerchantBillSummary>(`/distribution/withdrawal/${id}/bill-summary`),
}

// ==================== 店长申请宣传配置 ====================
export const shopOwnerPromoApi = {
  // 权益条目
  listBenefits: (merchant_id: number) =>
    http.get<T.ShopOwnerBenefit[]>('/distribution/shop-owner-promo/benefits', { merchant_id }),
  createBenefit: (data: T.ShopOwnerBenefit) =>
    http.post<{ id: number }>('/distribution/shop-owner-promo/benefits', data),
  updateBenefit: (id: number, data: Partial<T.ShopOwnerBenefit>) =>
    http.post('/distribution/shop-owner-promo/benefits/update', { id, ...data }),
  deleteBenefit: (id: number) =>
    http.post('/distribution/shop-owner-promo/benefits/delete', { id }),
  // 申请说明配置
  getApplyConfig: (merchant_id: number) =>
    http.get<T.ShopOwnerApplyConfig>('/distribution/shop-owner-promo/apply-config', { merchant_id }),
  saveApplyConfig: (data: T.ShopOwnerApplyConfig) =>
    http.post('/distribution/shop-owner-promo/apply-config', data),
  // 店长协议
  getAgreement: (merchant_id: number) =>
    http.get<T.ShopOwnerAgreement>('/distribution/shop-owner-promo/agreement', { merchant_id }),
  saveAgreement: (data: T.ShopOwnerAgreement) =>
    http.post('/distribution/shop-owner-promo/agreement', data),
}

// ==================== 通用任务配置 ====================
export const taskConfigApi = {
  list: (merchant_id: number, task_type?: string) =>
    http.get<T.TaskConfig[]>('/system/task-config', { merchant_id, task_type }),
  get: (id: number) => http.get<T.TaskConfig>(`/system/task-config/${id}`),
  create: (data: T.TaskConfig) => http.post<{ id: number }>('/system/task-config', data),
  update: (data: Partial<T.TaskConfig>) => http.post('/system/task-config', data), // ID在body中
  delete: (id: number) => http.post(`/system/task-config/${id}/delete`),
  stats: (merchant_id: number) =>
    http.get<T.TaskStatItem[]>('/system/task-config/stats', { merchant_id }),
}

// ==================== GCC积分兑换比例计划 ====================
export const gccRatioApi = {
  get: () => http.get<{ list: any[]; now: string; current_price: number }>('/system/gcc-ratio'),
  save: (items: { ratio_time: string; price_usdt: number }[]) =>
    http.put('/system/gcc-ratio', items),
}

// ==================== 阿里云实名认证配置 ====================
export const aliyunVerifyApi = {
  get: () => http.get<T.AliyunVerifyConfig>('/system/aliyun-verify-config'),
  save: (data: T.AliyunVerifyConfig) => http.post('/system/aliyun-verify-config', data),
  test: (data: T.AliyunVerifyTestReq) =>
    http.post<T.AliyunVerifyResult>('/system/aliyun-verify-config/test', data),
}

// ==================== 销售流程步骤 ====================
export const salesStepApi = {
  list: (merchant_id: number) =>
    http.get<T.SalesStep[]>('/system/sales-step', { merchant_id }),
  batchSave: (merchant_id: number, steps: T.SalesStep[]) =>
    http.post('/system/sales-step', { merchant_id, steps }),
  delete: (id: number) => http.post(`/system/sales-step/${id}/delete`),
}

// ==================== 温馨提示 ====================
export const warmTipApi = {
  list: (merchant_id: number, page_key?: string) =>
    http.get<T.WarmTip[]>('/system/warm-tip', { merchant_id, page_key }),
  create: (data: T.WarmTip) => http.post<{ id: number }>('/system/warm-tip', data),
  update: (data: Partial<T.WarmTip>) => http.post('/system/warm-tip', data), // ID在body中
  delete: (id: number) => http.post(`/system/warm-tip/${id}/delete`),
}

// ==================== 个人展位（平台后台）====================
export const personalSlotApi = {
  list: () => http.get<T.PersonalAdSlot[]>('/personal-slot'),
  create: (data: T.PersonalAdSlot) => http.post<{ id: number }>('/personal-slot', data),
  update: (data: Partial<T.PersonalAdSlot>) => http.post('/personal-slot/update', data), // ID在body中
  delete: (id: number) => http.delete(`/personal-slot/${id}`),
  // 订单
  listOrders: (params?: { merchant_id?: number; status?: number }) =>
    http.get<T.PersonalAdSlotOrder[]>('/personal-slot/order', params),
  auditOrder: (data: { id: number; status: number; remark?: string }) =>
    http.post('/personal-slot/order/audit', data),
  // 激活码
  listCodes: (params?: { status?: number }) =>
    http.get<T.SlotActivationCode[]>('/personal-slot/code', params),
  generateCodes: (data: { count: number; remark?: string }) =>
    http.post<{ count: number }>('/personal-slot/code/generate', data),
  disableCode: (data: { id: number }) => http.post('/personal-slot/code/disable', data),
}

// ==================== 商户端展示位管理 ====================
export const merchantSlotApi = {
  // 展位列表（含商品数量）
  list: () => http.post<{ list: T.MerchantSlotWithCount[]; total: number }>('/slot/list', {}),
  // 展位详情
  detail: (slotId: number) => http.post<T.PersonalAdSlot>('/slot/detail', { slot_id: slotId }),
  // 更新展位
  update: (slotId: number, data: { name?: string; status?: number }) =>
    http.post('/slot/update', { slot_id: slotId, ...data }),
  // 删除展位
  delete: (slotId: number) => http.post('/slot/delete', { slot_id: slotId }),
  // 展位商品列表（含SPU详情）
  products: (slotId: number) =>
    http.post<T.SlotProductWithSpu[]>('/slot/products', { slot_id: slotId }),
  // 添加商品到展位
  addProduct: (slotId: number, spuId: number, sort?: number) =>
    http.post('/slot/product/add', { slot_id: slotId, spu_id: spuId, sort }),
  // 从展位移除商品
  removeProduct: (slotId: number, spuId: number) =>
    http.post('/slot/product/remove', { slot_id: slotId, spu_id: spuId }),
  // 更新排序
  updateSort: (slotId: number, spuId: number, sort: number) =>
    http.post('/slot/product/sort', { slot_id: slotId, spu_id: spuId, sort }),
  // 随机填充指定展位
  random: (slotId: number, count?: number) =>
    http.post<{ added: number }>('/slot/random', { slot_id: slotId, count }),
  // 随机填充所有展位
  randomAll: (count?: number) =>
    http.post<{ slots_updated: number; total_added: number }>('/slot/random-all', { count }),
  // 购买/续费展位（code|price|discount）
  buy: (data: {
    slot_id?: number
    buy_type: 'code' | 'price' | 'discount'
    code_used?: string
    start_date?: string
    end_date?: string
  }) => http.post<{ slot_id?: number; order_id?: number; amount_paid?: number; days?: number; message?: string }>('/slot/buy', data),
  // 展位码扩展（新增一个展位）
  extendByCode: (code: string, slotId?: number) =>
    http.post<{ slot_id: number; name: string; message: string }>('/slot/code/extend', { code, slot_id: slotId }),
  // 商户商品列表（分页，用于选择）
  myProducts: (params?: { keyword?: string; page?: number; page_size?: number }) =>
    http.post<{ list: T.ProductSpu[]; total: number; page: number; page_size: number }>('/slot/my-products', params ?? {}),
}

// ==================== 星级规则（平台后台）====================
export const starRuleApi = {
  list: () => http.get<T.StarRule[]>('/star-rule'),
  create: (data: T.StarRule) => http.post<{ id: number }>('/star-rule', data),
  update: (data: T.StarRule) => http.post('/star-rule', data), // ID在body中
  delete: (id: number) => http.post(`/star-rule/${id}/delete`),
}

// ==================== 邀请奖励规则 ====================
export interface InviteRewardRule {
  id?: number
  level: number
  name: string
  invitee_count: number
  qualified_count: number
  star_threshold: number
  reward_amount: number
  is_enabled: 0 | 1
  created_at?: string
  updated_at?: string
}

export interface InviteRewardLog {
  id: number
  merchant_id: number
  rule_id: number
  level: number
  invitee_count_snapshot: number
  qualified_count_snapshot: number
  reward_amount: number
  granted_at: string
  created_at: string
}

export const inviteRewardApi = {
  listRules: () => http.get<InviteRewardRule[]>('/invite-reward/rule'),
  saveRule: (data: InviteRewardRule) =>
    http.post<InviteRewardRule>('/invite-reward/rule', data),
  deleteRule: (id: number) => http.post(`/invite-reward/rule/${id}/delete`),
  listLogs: (params: { merchant_id?: number; page?: number; page_size?: number }) =>
    http.get<{ list: InviteRewardLog[]; total: number }>('/invite-reward/log', params),
  evaluate: (merchantId: number) =>
    http.post(`/invite-reward/evaluate/${merchantId}`),
}

export const merchantStarApi = {
  list: (stars?: number) =>
    http.get<T.MerchantStarLevel[]>('/merchant-star', { stars }),
  getLogs: (merchant_id?: number) =>
    http.get<T.StarEvalLog[]>('/merchant-star/logs', { merchant_id }),
  get: (merchantId: number) =>
    http.get<{ star_level: T.MerchantStarLevel; metrics: T.MerchantMetricsSnapshot }>(`/merchant-star/${merchantId}`),
  set: (data: { merchant_id: number; stars: number; note?: string }) =>
    http.post('/merchant-star', data),
  evaluate: (data: { merchant_id: number }) =>
    http.post<{ changed: boolean; note: string }>('/merchant-star/evaluate', data),
  evaluateAll: () => http.post('/merchant-star/evaluate-all', {}),
}

// ==================== 统计数据 ====================
export interface AdminDashboardStats {
  merchant_count: number
  spu_count: number
  customer_count: number
  today_order_count: number
  today_order_amount: number
  pending_order_count: number
}

export interface MerchantLedgerIncome {
  currency: 'USDT'; basis: string
  net_usdt: string; order_usdt: string; team_usdt: string
  gross_order_usdt: string; gross_team_usdt: string
  order_recovered_usdt: string; team_recovered_usdt: string
  fund_inflow_usdt: string; fund_outflow_usdt: string
  fund_inflow_gcc: string; fund_outflow_gcc: string
}
export interface MerchantPeriodStats {
  ledger_income?: MerchantLedgerIncome
  income: number
  paid_amount: number
  order_count: number
  sub_commission: number
  sub_orders: number
}

export interface MerchantChartItem {
  ledger_income?: MerchantLedgerIncome
  date: string
  order_count: number
  order_amount: number
  sub_amount: number
  commission_amount: number
  click_count: number
}

export interface MerchantDashboardStats {
  income_schema?: string
  pending_order_usdt?: string; pending_team_usdt?: string
  pending_order_recovery_usdt?: string; pending_team_recovery_usdt?: string
  today: MerchantPeriodStats
  week: MerchantPeriodStats
  month: MerchantPeriodStats
  all_time: MerchantPeriodStats
  chart_data: MerchantChartItem[]
}

// 商户端每日数据统计（仅当日）
export interface MerchantRechargeStats {
  self_amount: number
  admin_amount: number
  reward_code_amount: number
  total_amount: number
  merchant_count: number
  count: number
}

export interface MerchantWithdrawStats {
  apply_amount: number
  approved_amount: number
  rejected_amount: number
  failed_amount: number
  pending_amount: number
}

export interface MerchantDailyStats {
  date: string
  new_merchant_count: number
  active_merchant_count: number
  real_name_verified_count: number
  recharge: MerchantRechargeStats
  first_recharge_merchant_count: number
  withdraw: MerchantWithdrawStats
}

// 商户端每日数据统计固化报表
export interface MerchantDailyReport {
  id: number
  report_date: string
  new_merchant_count: number
  recharge_self_amount: number
  recharge_admin_amount: number
  recharge_reward_code_amount: number
  recharge_total_amount: number
  recharge_merchant_count: number
  recharge_count: number
  first_recharge_merchant_count: number
  withdraw_apply_amount: number
  active_merchant_count: number
  real_name_verified_count: number
  withdraw_approved_amount: number
  withdraw_rejected_amount: number
  withdraw_failed_amount: number
  withdraw_pending_amount: number
  snapshot_at: string
  generation_mode: 'auto' | 'manual'
  generated_at: string
  generated_by_admin_id: number | null
}

// 单日详情：is_realtime=true 时是"今天"，走实时查询，generation_mode/generated_at/
// snapshot_at 不存在；is_realtime=false 时是历史固化报表。
export interface MerchantDailyReportDetail {
  is_realtime: boolean
  date: string
  new_merchant_count: number
  active_merchant_count: number
  real_name_verified_count: number
  recharge: MerchantRechargeStats
  first_recharge_merchant_count: number
  withdraw: MerchantWithdrawStats
  generation_mode?: 'auto' | 'manual'
  generated_at?: string
  snapshot_at?: string
}

export const statsApi = {
  // 管理后台全局统计
  adminDashboard: () =>
    http.get<AdminDashboardStats>('/stats/admin-dashboard'),
  // 管理后台指定商户统计
  adminMerchantDashboard: (merchant_id: number) =>
    http.get<MerchantDashboardStats>('/stats/admin-merchant-dashboard', { merchant_id }),
  // 商户端每日数据统计（仅当日，实时）
  merchantDaily: () =>
    http.get<MerchantDailyStats>('/stats/merchant-daily'),
  // 商户端每日数据统计报表列表（历史，分页+日期范围）
  merchantDailyReportList: (params: { start_date?: string; end_date?: string; page?: number; page_size?: number }) =>
    http.get<{ list: MerchantDailyReport[]; total: number; page: number; page_size: number }>('/stats/merchant-daily-report/list', params),
  // 单日详情（今天转发实时查询，历史读固化报表）
  merchantDailyReportDetail: (date: string) =>
    http.get<MerchantDailyReportDetail>('/stats/merchant-daily-report/detail', { date }),
  // 手动重算指定日期的报表（覆盖已有数据，仅限历史日期）
  merchantDailyReportRegenerate: (date: string) =>
    http.post('/stats/merchant-daily-report/regenerate', { date }),
  // 商品统计汇总（需要 spu_id）
  getProductStats: (spu_id: number, start_date?: string, end_date?: string) =>
    http.get<T.ProductStatsResp>('/stats/product', { spu_id, start_date, end_date }),
  // 商品每日明细
  getProductDaily: (params: T.ProductStatsQuery) =>
    http.get<T.ProductDailyStats[]>('/stats/product/daily', params),
  // 商品排行（按商户）
  getProductRanking: (merchant_id: number, params?: { start_date?: string; end_date?: string; order_by?: string; limit?: number }) =>
    http.get<T.ProductDailyStats[]>('/stats/product/ranking', { merchant_id, ...params }),
  // 店铺统计汇总（需要 shop_owner_id）
  getShopStats: (shop_owner_id: number, start_date?: string, end_date?: string) =>
    http.get<T.ShopStatsSummary>('/stats/shop', { shop_owner_id, start_date, end_date }),
  // 店铺每日明细
  getShopDaily: (params: T.ShopStatsQuery) =>
    http.get<T.ShopDailyStats[]>('/stats/shop/daily', params),
}

// ==================== 广告展位（平台后台）====================
export const adSlotApi = {
  list: (with_occupancy?: number) =>
    http.get<T.AdSlot[]>('/ad/slot', { with_occupancy }),
  create: (data: T.AdSlot) => http.post<{ id: number }>('/ad/slot', data),
  update: (data: Partial<T.AdSlot>) => http.post('/ad/slot/update', data), // ID在body中
  delete: (id: number) => http.post(`/ad/slot/${id}/delete`),
  // 展位订单审核
  listOrders: (params?: { merchant_id?: number; status?: number }) =>
    http.get<T.AdSlotOrder[]>('/ad/slot-order', params),
  auditOrder: (data: { id: number; status: number; remark?: string }) =>
    http.post('/ad/slot-order/audit', data),
}

// ==================== 推广活动（平台后台）====================
export const adCampaignAdminApi = {
  list: (params?: { merchant_id?: number; status?: number }) =>
    http.get<T.AdCampaign[]>('/ad/campaign', params),
  updateStatus: (data: { id: number; status: number }) =>
    http.post('/ad/campaign/status', data),
  stats: (merchant_id?: number) =>
    http.get('/ad/stats', { merchant_id }),
}

// ==================== 推广价格配置 ====================
export interface BudgetTier {
  value: number; est_min: number; est_max: number
  views_min: number; views_max: number
  clicks_min: number; clicks_max: number
  uv_min: number; uv_max: number
}
export const campaignConfigApi = {
  get: () => http.get<{ id: number; durations: number[]; budgets: BudgetTier[]; min_budget: number; currency: string; allow_balance: boolean; allow_tron: boolean }>('/ad/campaign-config'),
  save: (data: { id?: number; durations: number[]; budgets: BudgetTier[]; min_budget: number; currency: string; allow_balance: boolean; allow_tron: boolean }) =>
    http.post('/ad/campaign-config', data),
}

// ==================== 推流配置 ====================
export interface AnalyticsGrowthRange { min: number; max: number }
export interface StreamTier {
  label: string
  max_slots: number
  views_min: number; views_max: number
  clicks_min: number; clicks_max: number
  uv_min: number; uv_max: number
  source_ranges?: AnalyticsGrowthRange[]
  age_ranges?: AnalyticsGrowthRange[]
  gender_ranges?: AnalyticsGrowthRange[]
}
export const streamConfigApi = {
  get: () => http.get<{ tiers: StreamTier[] }>('/ad/stream-config'),
  save: (data: { tiers: StreamTier[] }) => http.post('/ad/stream-config', data),
}

// ==================== 搜索插队（平台后台）====================
export const searchInterceptApi = {
  list: (merchant_id?: number) =>
    http.get<T.SearchIntercept[]>('/ad/search-intercept', { merchant_id }),
  create: (data: T.SearchIntercept) =>
    http.post<{ id: number }>('/ad/search-intercept', data),
  update: (data: Partial<T.SearchIntercept>) =>
    http.post('/ad/search-intercept/update', data),
  delete: (id: number) => http.post(`/ad/search-intercept/${id}/delete`),
}

// ==================== 商户推广管理（管理员代理视角，admin 路由，无加密）====================
export const merchantAdApi = {
  // 推广活动
  listCampaigns: (merchant_id: number, status?: number) =>
    http.get<T.AdCampaign[]>('/ad/merchant-campaign', { merchant_id, status }),
  getCampaign: (id: number) =>
    http.get<T.AdCampaign>(`/ad/merchant-campaign/${id}`),
  createCampaign: (merchant_id: number, data: T.AdCampaign) =>
    http.post<{ id: number }>(`/ad/merchant-campaign?merchant_id=${merchant_id}`, data),
  updateCampaign: (data: T.AdCampaign & { merchant_id: number }) =>
    http.post('/ad/merchant-campaign/update', data),
  deleteCampaign: (id: number) =>
    http.post(`/ad/merchant-campaign/${id}/delete`, { id }),
  campaignStats: (id: number, start?: string, end?: string) =>
    http.get<T.AdDailyStat[]>(`/ad/merchant-campaign/${id}/stats`, { start, end }),
  // 展位
  listSlots: () => http.get<T.AdSlot[]>('/ad/merchant-slot'),
  buySlot: (merchant_id: number, data: T.AdSlotOrder) =>
    http.post<{ id: number; total_amount: number }>(`/ad/merchant-slot-order?merchant_id=${merchant_id}`, data),
  listOrders: (merchant_id: number, status?: number) =>
    http.get<T.AdSlotOrder[]>('/ad/merchant-slot-order', { merchant_id, status }),
  // 受众预估
  estimateReach: (merchant_id: number, config: T.AdTargetConfig) =>
    http.post<T.AdReachResult>(`/ad/merchant-estimate-reach?merchant_id=${merchant_id}`, config),
  // 推广活动审核（admin）
  listPendingCampaigns: (status?: number) =>
    http.get<T.AdCampaign[]>('/ad/campaign', { status: status ?? 2 }),
  reviewCampaign: (data: { id: number; action: 'approve' | 'reject'; remark?: string }) =>
    http.post('/ad/campaign/review', data),
}

// ==================== 签到配置 ====================
export const signInConfigApi = {
  getConfig: () =>
    http.get<T.SignInConfig>('/system/sign-in/config'),
  saveConfig: (data: Partial<T.SignInConfig>) =>
    http.post('/system/sign-in/config', data),
  getStats: (params: { merchant_id: number; start_date?: string; end_date?: string }) =>
    http.get<T.SignInStatsResp>('/system/sign-in/stats', params),
}

// ==================== 商户互助助力配置 ====================
export interface BoostRewardTier {
  day_from: number
  day_to: number // -1 = 无上界（封顶档）
  points: number
}
export interface BoostRewardConfig {
  is_enabled: number
  max_received_per_day: number
  tiers: BoostRewardTier[]
}
export const boostRewardConfigApi = {
  getConfig: () =>
    http.get<BoostRewardConfig>('/system/boost-reward-config'),
  saveConfig: (data: BoostRewardConfig) =>
    http.post('/system/boost-reward-config', data),
}

export const staffApi = {
  list: (params: { merchant_id: number; page?: number; page_size?: number }) =>
    http.get<T.PageResult<T.ShopStaff>>('/staff', params),
  updateStatus: (data: { id: number; status: number }) =>
    http.post('/staff/status', data),
}

// ==================== App 版本控制 ====================
export const appVersionApi = {
  list: (params?: { platform?: string; merchant_id?: number }) =>
    http.get<T.AppVersion[]>('/system/app-version', params),
  create: (data: T.AppVersion) =>
    http.post<{ id: number }>('/system/app-version', data),
  update: (data: Partial<T.AppVersion>) =>
    http.post('/system/app-version/update', data),
  delete: (id: number) =>
    http.post(`/system/app-version/${id}/delete`),
}

// ==================== OTA 热更新版本 ====================
export const otaVersionApi = {
  list: (params?: { app_id?: string }) =>
    http.get<T.OtaVersion[]>('/system/ota-version', params),
  create: (data: Partial<T.OtaVersion>) =>
    http.post<{ id: number }>('/system/ota-version', data),
  update: (data: Partial<T.OtaVersion>) =>
    http.post('/system/ota-version/update', data),
  delete: (id: number) =>
    http.post(`/system/ota-version/${id}/delete`),
}

// ==================== 订单管理 ====================
export const orderApi = {
  list: (params?: {
    page?: number; page_size?: number; status?: number;
    customer_name?: string; order_no?: string;
  }) => http.post<{ list: T.Order[]; total: number }>('/order', params || {}),
  detail: (id: number) => http.post<any>('/order/detail', { order_id: id }),
  ship: (data: { id: number; express_company?: string; courier_code?: string; tracking_no?: string; expected_revision?: number; expected_edit_revision?: number }) =>
    http.post('/order/ship', data),
  cancel: (data: { id: number }) => http.post('/order/cancel', data),
  createReturn: (data: { order_id: number; return_logistics_time: string }) =>
    http.post('/order/return', data),
  cancelReturn: (data: { return_id: number }) =>
    http.post('/order/return/cancel', data),
}

// ==================== 订单配置（退货窗口天数）====================
export const orderConfigApi = {
  get: () => http.get<{ return_window_days: number }>('/system/order-config'),
  save: (data: { return_window_days: number }) => http.post('/system/order-config', data),
}

// ==================== 商户仪表盘 ====================
export const merchantDashboardApi = {
  // 获取商户仪表盘统计（使用商户列表数据模拟）
  getDashboard: (merchant_id: number) =>
    http.get<T.MerchantDashboard>('/merchant/' + merchant_id),
  // 获取商户详情
  getMerchantDetail: (id: number) =>
    http.get<T.Merchant>(`/merchant/${id}`),
}

// ==================== 线路Key ====================
export const lineKeyApi = {
  list: (params?: { page?: number; page_size?: number; status?: number }) =>
    http.get<T.PageResult<T.LineKey>>('/system/line-key/list', params ?? {}),
  generate: (data: { count: number; expire_at?: string }) =>
    http.post<{ count: number }>('/system/line-key/generate', data),
  delete: (id: number) => http.delete(`/system/line-key/${id}`),
}

// ==================== 奖励码 ====================
export const rewardCodeApi = {
  list: (params?: { merchant_id?: number; type?: number }) =>
    http.get<T.RewardCode[]>('/system/reward-code', params ?? {}),
  create: (data: Partial<T.RewardCode>) => http.post('/system/reward-code', data),
  update: (id: number, data: Partial<T.RewardCode>) => http.put(`/system/reward-code/${id}`, data),
  delete: (id: number) => http.post(`/system/reward-code/${id}/delete`),
  batchGenerate: (data: { usdt_amount: number; gcc_amount: number; quantity: number; expire_at?: string }) =>
    http.post<{ count: number }>('/system/reward-code/batch-generate', data),
}

// ==================== TRON 支付 ====================
export const tronApi = {
  addHotWallet: (data: { address: string; priv_key: string; label?: string }) =>
    http.post<T.TronHotWallet>('/tron/hotwallet', data),
  listHotWallets: () => http.get<T.TronHotWallet[]>('/tron/hotwallet'),
  deleteHotWallet: (id: number) => http.delete(`/tron/hotwallet/${id}`),
  addPoolWallet: (data: { address: string; label?: string }) =>
    http.post<T.TronWallet>('/tron/wallet/pool', data),
  listPoolWallets: () => http.get<T.TronWallet[]>('/tron/wallet/pool'),
  deletePoolWallet: (id: number) => http.delete(`/tron/wallet/pool/${id}`),
  createOrder: (data: {
    currency?: string
    amount: number
    merchant_id?: number
    customer_id?: number
    remark?: string
    expire_min?: number
  }) => http.post<{ order: T.TronPaymentOrder; qr: T.TronQRResp }>('/tron/payment/order', data),
  listOrders: (params?: { status?: number; start_date?: string; end_date?: string; page?: number; page_size?: number }) =>
    http.get<{ list: T.TronPaymentOrder[]; total: number }>('/tron/payment/order', params),
  queryStatus: (order_no: string) =>
    http.get<T.TronPaymentOrder>('/tron/payment/status', { order_no }),
  transfer: (data: { to_address: string; currency: string; amount: number }) =>
    http.post<T.TronTransferRecord>('/tron/transfer', data),
  listTransfers: (page?: number, page_size?: number) =>
    http.get<{ list: T.TronTransferRecord[]; total: number }>('/tron/transfer', { page, page_size }),
}

export const chatApi = {
  listSessions: (params?: { page?: number; page_size?: number; status?: number }) =>
    http.get<{ list: T.ChatSession[]; total: number; page: number; page_size: number }>('/system/chat/sessions', params),
  listMessages: (sessionId: number, params?: { page?: number; page_size?: number }) =>
    http.get<{ list: T.ChatMessage[]; total: number }>(`/system/chat/sessions/${sessionId}/messages`, params),
  sendMessage: (sessionId: number, data: { msg_type?: number; content?: string; image_url?: string }) =>
    http.post<T.ChatMessage>(`/system/chat/sessions/${sessionId}/send`, data),
  closeSession: (sessionId: number) =>
    http.post(`/system/chat/sessions/${sessionId}/close`),
  listMerchants: (params?: { page?: number; page_size?: number; keyword?: string }) =>
    http.get<{ list: T.MerchantChatView[]; total: number }>('/system/chat/merchants', params),
  searchUsers: (keyword: string) =>
    http.get<{ list: { party_type: number; party_id: number; name: string; email: string; extra: string }[] }>('/system/chat/user-search', { keyword }),
  ensureSession: (data: { party_type: number; party_id: number; party_name: string }) =>
    http.post<T.ChatSession>('/system/chat/sessions/ensure', data),
}

// ==================== 团队等级 & 奖励配置 ====================
export const teamGradeApi = {
  // 团队等级配置
  listGrades: (merchant_id: number) =>
    http.get<T.TeamGradeConfig[]>('/distribution/team-grade', { merchant_id }),
  createGrade: (data: Partial<T.TeamGradeConfig>) =>
    http.post<{ id: number }>('/distribution/team-grade', data),
  updateGrade: (id: number, data: Partial<T.TeamGradeConfig>) =>
    http.put(`/distribution/team-grade/${id}`, data),
  deleteGrade: (id: number) =>
    http.delete(`/distribution/team-grade/${id}`),
  // 团队奖励配置
  listRewardConfigs: (merchant_id: number) =>
    http.get<T.TeamRewardConfig[]>('/distribution/team-reward-config', { merchant_id }),
  createRewardConfig: (data: Partial<T.TeamRewardConfig>) =>
    http.post<{ id: number }>('/distribution/team-reward-config', data),
  updateRewardConfig: (id: number, data: Partial<T.TeamRewardConfig>) =>
    http.put(`/distribution/team-reward-config/${id}`, data),
  deleteRewardConfig: (id: number) =>
    http.delete(`/distribution/team-reward-config/${id}`),
  // 奖励记录
  listRecords: (params: {
    merchant_id?: number; distributor_id?: number; period?: string;
    page?: number; page_size?: number;
  }) => http.get<T.PageResult<T.TeamRewardRecord>>('/distribution/team-reward-records', params),
  manualGrant: (data: { distributor_id: number; remark?: string }) =>
    http.post('/distribution/team-reward/manual-grant', data),
  grantMonthly: (data: { merchant_id: number; period?: string }) =>
    http.post<{ granted: number; period: string }>('/distribution/team-reward/grant-monthly', data),
}

// ==================== 商品板块配置 ====================

export const goodsSectionApi = {
  // 板块列表
  list: (params?: { status?: number }) =>
    http.post<T.GoodsSection[]>('/goods-section/list', params ?? {}),
  // 板块详情
  detail: (id: number) =>
    http.get<T.GoodsSection>(`/goods-section/${id}`),
  // 创建板块
  create: (data: Partial<T.GoodsSection>) =>
    http.post<{ id: number }>('/goods-section/create', data),
  // 更新板块
  update: (data: Partial<T.GoodsSection> & { id: number }) =>
    http.post('/goods-section/update', data),
  // 删除板块
  delete: (id: number) =>
    http.post('/goods-section/delete', { id }),
  // 数据指标列表
  listData: (section_id: number) =>
    http.post<T.GoodsSectionData[]>('/goods-section/data', { section_id }),
  // 批量更新数据指标
  batchUpdateData: (section_id: number, items: T.GoodsSectionData[]) =>
    http.post('/goods-section/data/batch-update', { section_id, items }),
  // 板块关联商品列表（支持分类筛选、分页）
  listProducts: (params: { section_id: number; category_id?: number; keyword?: string; page?: number; page_size?: number }) =>
    http.post<{ list: T.ProductSpu[]; total: number; page: number; page_size: number }>(
      '/goods-section/products', params
    ),
  // 批量添加商品到板块
  addProducts: (section_id: number, spu_ids: number[]) =>
    http.post('/goods-section/products/add', { section_id, spu_ids }),
  // 从板块移除商品
  removeProduct: (section_id: number, spu_id: number) =>
    http.post('/goods-section/products/remove', { section_id, spu_id }),
}

// ==================== 推荐板块管理（热门商品/猜你喜欢）====================

export const recommendSectionApi = {
  // 板块列表
  list: (params?: { section_type?: string; merchant_id?: number; status?: number }) =>
    http.post<T.RecommendSection[]>('/recommend-section/list', params ?? {}),
  // 板块详情
  detail: (id: number) =>
    http.post<T.RecommendSection>('/recommend-section/detail', { id }),
  // 创建板块
  create: (data: Partial<T.RecommendSection>) =>
    http.post<{ id: number }>('/recommend-section/create', data),
  // 更新板块
  update: (data: Partial<T.RecommendSection> & { id: number }) =>
    http.post('/recommend-section/update', data),
  // 删除板块
  delete: (id: number) =>
    http.post('/recommend-section/delete', { id }),
  // 推荐板块关联商品列表（支持分类筛选、分页）
  listProducts: (params: { section_id: number; category_id?: number; keyword?: string; page?: number; page_size?: number }) =>
    http.post<{ list: T.ProductSpu[]; total: number; page: number; page_size: number }>(
      '/recommend-section/products', params
    ),
  // 批量添加商品到板块
  addProducts: (section_id: number, spu_ids: number[]) =>
    http.post('/recommend-section/products/add', { section_id, spu_ids }),
  // 从板块移除商品
  removeProduct: (section_id: number, spu_id: number) =>
    http.post('/recommend-section/products/remove', { section_id, spu_id }),
  // 热搜关键词列表
  listKeywords: (params: { section_id: number; status?: number }) =>
    http.post<T.SearchKeyword[]>('/recommend-section/keywords', params),
  // 新增热搜关键词
  createKeyword: (data: { section_id: number; keyword: string; sort?: number; status?: number }) =>
    http.post<T.SearchKeyword>('/recommend-section/keyword/create', data),
  // 更新热搜关键词
  updateKeyword: (data: { id: number; keyword?: string; sort?: number; status?: number }) =>
    http.post('/recommend-section/keyword/update', data),
  // 删除热搜关键词
  deleteKeyword: (id: number) =>
    http.post('/recommend-section/keyword/delete', { id }),
}

// ==================== 消息模板 + 商户群发任务 ====================

export interface MsgTemplate {
  id: number
  name: string
  title: string
  content: string
  status: 0 | 1
  created_by?: number
  created_at?: string
  updated_at?: string
}

export interface MerchantTask {
  id: number
  name: string
  template_id: number
  template_title: string
  template_content: string
  logistics_group_id: number // 0 = 未关联物流分组
  total_count: number
  sent_count: number
  failed_count: number
  cancelled_count: number
  start_at: string
  end_at: string
  status: 0 | 1 | 2 // 0=进行中 1=已完成 2=已取消
  created_by?: number
  created_at?: string
  updated_at?: string
}

export interface MerchantTaskRecipient {
  id: number
  task_id: number
  merchant_id: number
  merchant_name: string
  scheduled_at: string
  sent_at: string | null
  status: 0 | 1 | 2 | 3 // 0=待发送 1=已发送 2=失败 3=已取消
  error_msg: string
  message_id: number
  created_at: string
  updated_at: string
}

export const msgTemplateApi = {
  list: (params: { page?: number; page_size?: number; keyword?: string; status?: number }) =>
    http.get<{ total: number; list: MsgTemplate[] }>('/msg-template/list', params),
  enabled: () => http.get<MsgTemplate[]>('/msg-template/enabled'),
  get: (id: number) => http.get<MsgTemplate>(`/msg-template/${id}`),
  save: (data: Partial<MsgTemplate>) => http.post<MsgTemplate>('/msg-template', data),
  delete: (id: number) => http.post(`/msg-template/${id}/delete`),
}

export const merchantTaskApi = {
  list: (params: { page?: number; page_size?: number; keyword?: string; status?: number }) =>
    http.get<{ total: number; list: MerchantTask[] }>('/merchant-task/list', params),
  get: (id: number) => http.get<MerchantTask>(`/merchant-task/${id}`),
  items: (id: number, params: { page?: number; page_size?: number; status?: number }) =>
    http.get<{ total: number; list: MerchantTaskRecipient[] }>(`/merchant-task/${id}/items`, params),
  create: (data: {
    name?: string
    template_id: number
    logistics_group_id?: number
    dispatch_now?: boolean
    dispatch_window_hours?: number
    test_customer_id?: number
    address_id?: number
    logistics_mode?: string
    merchant_ids: number[]
  }) => http.post<MerchantTask>('/merchant-task', data),
  cancel: (id: number) => http.post(`/merchant-task/${id}/cancel`),
  clone: (id: number, name?: string) => http.post<MerchantTask>(`/merchant-task/${id}/clone`, { name }),
}

// ==================== 单发商户通知 ====================
export const merchantNotifyApi = {
  send: (data: { merchant_id: number; type: string; title: string; content: string }) =>
    http.post('/merchant-message/send', data),
}

// ==================== 团队分账 & 奖励配置 ====================
export interface TeamOrderProfitSetting {
  id: number
  merchant_id: number
  level: number
  dist_rate: number
  is_enabled: number
  remark: string
}

export interface MerchantTeamStats {
  merchant_id: number
  merchant_name: string
  direct_count: number
  team_order_count: number
  team_order_amount: number
  total_balance: number
  pending_dist_count: number
}

export interface OrderDistributionLog {
  id: number
  order_id: number
  order_no: string
  merchant_id: number
  shop_owner_id: number
  parent_merchant_id: number
  actor_type: number
  actor_id: number
  actor_name: string
  order_amount: number
  dist_amount: number
  dist_rate: number
  dist_status: number
  dist_error: string
  dist_time: string | null
  remark: string
  created_at: string
}

export interface TeamPayoutOrder {
  id: string; order_no: string; merchant_id: string; pay_amount: string; payment_asset: 'USDT' | 'GCC'
  principal_state: string; principal_confirmed_at_utc: string; payout_due_at_utc: string
  payout_wait_days: number | null; state: string; reason: string; can_process: boolean; remaining_allocations: number
}
export interface TeamPayoutOverview {
  pending_count: number; total_distributed: string; merchant_distributed: string
  team_distributed: string; recovered: string; log_count: number; currency: 'USDT'
}
export interface TeamPayoutLog {
  id: string; order_id: string; order_no: string; merchant_id: string; layer: number
  kind: 'RELEASE' | 'RECOVERY'; amount: string; currency: 'USDT'; ledger_id: string; created_at_utc: string
}

export const teamDistributionApi = {
  // 商户端
  getOverview: () => http.get<{
    stats: MerchantTeamStats
    l1_config: TeamOrderProfitSetting | null
    l2_config: TeamOrderProfitSetting | null
  }>('/merchant/team/distribution/overview'),

  getPendingOrders: (params: { page?: number; page_size?: number }) =>
    http.get<{ total: number; list: any[] }>('/merchant/team/distribution/pending-orders', params),

  doDistribute: (order_id: number) =>
    http.post('/merchant/team/distribution/do', { order_id }),

  getDistributionLog: (params: { page?: number; page_size?: number; status?: number }) =>
    http.get<{ total: number; list: OrderDistributionLog[] }>('/merchant/team/distribution/log', params),

  getRewardConfig: () => http.get<{ settings: TeamOrderProfitSetting[] }>('/merchant/team/distribution/reward-config'),

  saveRewardConfig: (settings: Partial<TeamOrderProfitSetting>[]) =>
    http.post('/merchant/team/distribution/reward-config', settings),

  // 后台
  adminGetOverview: () =>
    http.get<TeamPayoutOverview>('/system/team/overview'),

  adminGetPendingOrders: (params: { page?: number; page_size?: number }) =>
    http.get<{ total: number; list: TeamPayoutOrder[] }>('/system/team/pending-orders', params),

  adminGetRewardConfig: () =>
    http.get<GlobalTeamRewardConfig | { configured: false }>('/system/team/reward-config'),

  adminSaveRewardConfig: (settings: { rates: [string, string, string, string]; return_window_days: number }) =>
    http.post<GlobalTeamRewardConfig>('/system/team/reward-config', settings),

  adminRewardConfigHistory: (page = 1, page_size = 20) =>
    http.get<{ list: GlobalTeamRewardConfig[]; total: string; page: number; page_size: number }>('/system/team/reward-config/history', { page, page_size }),

  adminDoDistribute: (order_id: string) =>
    http.post<{ order: TeamPayoutOrder; message: string }>('/system/team/distribution/process', { order_id }),

  adminGetDistributionLogs: (params: { page?: number; page_size?: number; status?: number }) =>
    http.get<{ total: number; list: TeamPayoutLog[] }>('/system/team/distribution-logs', params),
}

// ==================== 系统通知 ====================
export type SystemNotification = {
  id: number
  type: number
  title: string
  content: string
  is_read: boolean
  created_at: string
}

export const notificationApi = {
  unreadCount: () =>
    http.get<{ count: number }>('/system/notifications/unread-count'),
  list: (page = 1, pageSize = 10) =>
    http.get<{ total: number; page: number; page_size: number; list: SystemNotification[] }>(
      '/system/notifications',
      { page, page_size: pageSize },
    ),
  readAll: () => http.post('/system/notifications/read-all', {}),
}

// ==================== 侧边栏待办角标 ====================
// 数据源是各业务表的实时待审记录数，与上面的通知铃铛（unreadCount）不是
// 同一件事：铃铛是"来了新的"事件提醒，标记已读后消失；这里是"还剩多少"
// 的状态展示，只要业务表里还有待审记录就一直显示，与是否读过通知无关。
export type PendingCounts = {
  withdrawal: number
  dispute_arbitration: number
  ad_campaign: number
  ad_slot_order: number
}

export const pendingCountApi = {
  get: () => http.get<PendingCounts>('/system/pending-counts'),
}

export type GlobalTeamRewardConfig = { id: string; effective_at: string; rates: [string, string, string, string]; return_window_days: number; actor_id: string; previous_id: string }
